import React, { useState, useEffect, useCallback } from 'react';
import Modal from '../components/Modal';
import api from '../hooks/api';

const emptyForm = { name: '', email: '', password: '', role: 'staff', branch_id: '', department_id: '', is_active: true };

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [modal, setModal] = useState(null);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    const [u, b, d] = await Promise.all([api.get('/api/users'), api.get('/api/branches'), api.get('/api/departments')]);
    setUsers(u); setBranches(b); setDepartments(d);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filteredDepts = departments.filter(d => !form.branch_id || d.branch_id === form.branch_id);

  function openAdd() { setForm(emptyForm); setEditId(null); setError(''); setModal(true); }

  function openEdit(u) {
    setForm({ name: u.name, email: u.email, password: '', role: u.role, branch_id: u.branch_id || '', department_id: u.department_id || '', is_active: !!u.is_active });
    setEditId(u.id); setError(''); setModal(true);
  }

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSave(e) {
    e.preventDefault(); setError('');
    try {
      const payload = { ...form, branch_id: form.branch_id || null, department_id: form.department_id || null };
      if (!editId && !payload.password) { setError('Password is required'); return; }
      if (editId) { if (!payload.password) delete payload.password; await api.put('/api/users/' + editId, payload); }
      else await api.post('/api/users', payload);
      setModal(null); load();
    } catch (err) { setError(err.message); }
  }

  async function handleDelete(u) {
    if (!window.confirm(`Delete user "${u.name}"?`)) return;
    try { await api.delete('/api/users/' + u.id); load(); }
    catch (err) { alert(err.message); }
  }

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title" style={{ margin: 0 }}>Users</h1>
        <button className="btn btn-primary" onClick={openAdd}>+ Add User</button>
      </div>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr><th>Name</th><th>Email</th><th>Role</th><th>Branch</th><th>Department</th><th>Status</th><th>Created</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id}>
                <td style={{ fontWeight: 600 }}>{u.name}</td>
                <td>{u.email}</td>
                <td><span className={`badge badge-role badge-${u.role}`}>{u.role}</span></td>
                <td>{u.branch_name || '—'}</td>
                <td>{u.department_name || '—'}</td>
                <td><span className={`badge ${u.is_active ? 'badge-active' : 'badge-inactive'}`}>{u.is_active ? 'Active' : 'Inactive'}</span></td>
                <td>{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => openEdit(u)}>Edit</button>
                  <button className="btn btn-danger btn-sm" style={{ marginLeft: 6 }} onClick={() => handleDelete(u)}>Delete</button>
                </td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan={8} className="empty">No users found</td></tr>}
          </tbody>
        </table>
      </div>

      {modal && (
        <Modal title={editId ? 'Edit User' : 'Add User'} onClose={() => setModal(null)}>
          {error && <div className="alert alert-error">{error}</div>}
          <form onSubmit={handleSave}>
            <div className="form-group">
              <label>Name *</label>
              <input value={form.name} onChange={e => set('name', e.target.value)} required autoFocus />
            </div>
            <div className="form-group">
              <label>Email *</label>
              <input type="email" value={form.email} onChange={e => set('email', e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Password {editId ? '(leave blank to keep existing)' : '*'}</label>
              <input type="password" value={form.password} onChange={e => set('password', e.target.value)} required={!editId} autoComplete="new-password" />
            </div>
            <div className="form-group">
              <label>Role *</label>
              <select value={form.role} onChange={e => set('role', e.target.value)} required>
                <option value="admin">Admin</option>
                <option value="manager">Manager</option>
                <option value="staff">Staff</option>
              </select>
            </div>
            <div className="form-group">
              <label>Branch</label>
              <select value={form.branch_id} onChange={e => { set('branch_id', e.target.value); set('department_id', ''); }}>
                <option value="">All branches (admin)</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Department</label>
              <select value={form.department_id} onChange={e => set('department_id', e.target.value)}>
                <option value="">None</option>
                {filteredDepts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            {editId && (
              <div className="form-check">
                <input type="checkbox" id="user_active" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} />
                <label htmlFor="user_active">Active</label>
              </div>
            )}
            <div className="form-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
              <button type="submit" className="btn btn-primary">{editId ? 'Save Changes' : 'Add User'}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
